import { useServerRequest } from '../api'
import { type GetHookResponse } from '../hooks'
import { plansKey } from '../keys'
import { useTantalimQuery } from '../query'
import { useQueries } from '@tanstack/react-query'
import {
  type PlanScopeId,
  type PlanData,
  type TeamId,
  DAY,
  HOUR,
  toResponseError,
  type PlanPeriodValue,
  isDefined,
  type PlanListInput,
  type PlanListSuccess,
  type ResponseError,
} from '@terros/common'

export function usePlans(scopeId?: PlanScopeId): GetHookResponse<PlanData[]> {
  return useTantalimQuery<PlanListInput, PlanListSuccess, PlanData[]>({
    url: '/plan/list',
    queryKey: plansKey({ scopeId }),
    input: { version: 2, scopeId },
    convertResponse: (data) => data.plans,
  })
}

type UseTeamsPlansResult = {
  data: PlanData[]
  isLoading: boolean
  refetch: () => void
  isFetching: boolean
  error?: ResponseError
}

export type UseTeamsPlansInput = { period: PlanPeriodValue } & (
  | {
      type: 'user'
      teamId: TeamId
    }
  | {
      type: 'subteam'
      teamIds: TeamId[]
    }
)

export function useTeamsQuarterlyPlans(input: UseTeamsPlansInput): UseTeamsPlansResult {
  const serverRequest = useServerRequest<PlanListInput, PlanListSuccess>('/plan/list')
  const { period } = input
  const queriesInput = toQueryInputs(input)

  return useQueries({
    queries: queriesInput.map((query) => ({
      queryKey: plansKey(query),
      queryFn: async () => {
        const res = await serverRequest({ version: 2, ...query })
        return res.plans
      },
      staleTime: HOUR,
      gcTime: DAY,
      retry: false,
    })),
    combine: (results) => {
      const error = results.find((result) => result.error)
      return {
        data: results
          .flatMap((result) => result.data)
          .filter(isDefined)
          .filter((plan) => {
            if (isDefined(period) && plan.period !== period) return false
            return true
          }),
        isLoading: results.some((result) => result.isLoading),
        error: error ? toResponseError(error) : undefined,
        isFetching: results.some((result) => result.isFetching),
        refetch: () => results.forEach((result) => result.refetch()),
      }
    },
  })
}

const toQueryInputs = (input: UseTeamsPlansInput): PlanListInput[] => {
  if (input.type === 'subteam') {
    return input.teamIds.map((teamId) => ({ scopeId: teamId, period: input.period }))
  }
  return [{ scopeId: input.teamId, period: input.period, includeTeamMembers: true }]
}
